import React, {useReducer, useState} from 'react';
import lz from 'lzutf8';
import {toast} from 'react-toastify';
import {QrCodeIcon} from '@heroicons/react/24/outline';
import {useEditor} from '@craftjs/core';
import {getGetProjectsQueryKey, usePublishProject, useSaveProjectState} from 'api/generated';
import {ButtonWithOptions, Transitioner} from 'components';
import {queryClient} from 'App';

import ProjectDeviceToggle from './Topbar/Project.Device.Toggle';
import useProjectStore from '../stores/useProjectStore';
import ModalQRShare from './Topbar/Modal.QR.Share';

interface ITopbarProps {}

const Topbar = ({}: ITopbarProps) => {
  const [projectState, setProjectState] = useState<'preview' | 'publish'>();
  const [qrModal, toggleQRModal] = useReducer(s => !s, false);

  const projectUid = useProjectStore(s => s.uid);
  const projectName = useProjectStore(s => s.name);
  const previewState = useProjectStore(s => s.previewState);
  const meshes = useProjectStore(s => s.meshes) ?? [];
  const isDirty = useProjectStore(s => s.isEditorDirty || s.isMeshesDirty);
  const isPublishable = useProjectStore(s => s.isPublishable);
  const updateProject = useProjectStore(s => s.updateProject);
  const updatePublished = useProjectStore(s => s.updatePublished);

  const {query} = useEditor();

  const saveProjectState = useSaveProjectState();
  const handleSave = async () => {
    const contentToken = (() => {
      const json = query.serialize();
      return lz.encodeBase64(lz.compress(json));
    })();
    const meshesUids = (() => {
      const assignedMeshes = meshes.filter(m => m.assigned);
      return assignedMeshes.map(m => m.uid);
    })();
    const {data: res} = await saveProjectState.mutateAsync({
      projectUid,
      data: {
        published: previewState?.published,
        contentToken,
        resourceUids: [],
        meshesUids,
      },
    });
    if (res.data) {
      toast.success('Project saved');
      updateProject(res.data);
      queryClient.invalidateQueries({queryKey: [getGetProjectsQueryKey({filter: 'all'})[0]]});
    } else if (res.errors) {
      toast.error(res.errors[0].detail);
    }
  };

  const publishProject = usePublishProject();
  const handlePublish = async () => {
    const {data: res} = await publishProject.mutateAsync({projectUid});
    if (res.data) {
      toast.success('Project published');
      updatePublished(res.data);
      queryClient.invalidateQueries({queryKey: [getGetProjectsQueryKey({filter: 'all'})[0]]});
      handleShowQR('publish');
    } else if (res.errors) {
      toast.error(res.errors[0].detail);
    }
  };

  const handleShowQR = (state: 'preview' | 'publish') => {
    setProjectState(state);
    toggleQRModal();
  };

  const loading = saveProjectState.isPending || publishProject.isPending;

  return (
    <section className="pl-17 h-15.5 z-40 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-neutral-950 shadow-xs">
      <Transitioner fromY={-50}>
        <div className="flex items-center gap-x-6 px-4 py-3 sm:px-6">
          <div className="w-full flex items-center justify-between">
            <p className="w-40 truncate dark:text-white w-50">{projectName}</p>
            <ProjectDeviceToggle />
            <div className="flex gap-1.5">
              <ButtonWithOptions
                text="Save"
                size="small"
                onClick={handleSave}
                loading={loading}
                disabled={!isDirty}
                options={[[{icon: QrCodeIcon, text: 'Preview', onClick: () => handleShowQR('preview')}]]}
              />
              <ButtonWithOptions
                text="Publish"
                size="small"
                onClick={handlePublish}
                loading={loading}
                disabled={!isPublishable}
                options={[[{icon: QrCodeIcon, text: 'Share', onClick: () => handleShowQR('publish')}]]}
              />
            </div>
          </div>
        </div>
      </Transitioner>
      <ModalQRShare visible={qrModal} onClose={toggleQRModal} state={projectState} />
    </section>
  );
};

export default React.memo(Topbar);
